import PropTypes from 'prop-types'

import {List, Map} from 'immutable'
import {useEffect, useState} from 'react'
import {useForm, FormProvider, Controller} from 'react-hook-form'

import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import Button from '../../../../../shared_components/mui_base_components/button/Button'
import ManageItemPermissions from './ManageItemPermissions'
import PermissionSelect from '../../permissions/specific_item/shared_components/PermissionSelect'
import ManageVaultExtensionPermission from './ManageVaultExtensionPermission'

import warningIcon from '../../../../assets/warning-icon-black.svg'
import {existingPermissionOptions} from '../../../../../lib/deputyPermissionTools'

import './managePermissionsForm.scss'

const options = ([{value: ''}]).concat(existingPermissionOptions)

const permissionByOption = ({permissions, option}) =>
  Object.values(permissions).every(permission =>
    permission.value === option.value
  )

const selectAllOption = permissions => {
  if (permissionByOption({permissions, option: options[1]}))
    return options[1]
  else if (permissionByOption({permissions, option: options[2]}))
    return options[2]
  else if (permissionByOption({permissions, option: options[3]}))
    return options[3]
  else
    return options[0]
}

const ManagePermissionsForm = ({
  buttonText = 'Next',
  cancelButtonText = 'Back',
  ...otherProps
}) => {
  const methods = useForm({
    defaultValues: {
      selectAll: selectAllOption(otherProps.permissions),
      ...otherProps.permissions
    }
  })

  const {
    control,
    formState: {isDirty},
    getValues,
    handleSubmit,
    setValue,
    watch
  } = methods

  const [
    isSaveButtonDisabled, setIsSaveButtonDisabled
  ] = useState(!otherProps.isAddDeputy)

  const permissionValues = getValues(Object.keys(otherProps.permissions))
  const selectAllValue = watch('selectAll')

  useEffect(() => {
    if (selectAllValue.value &&
      Object.values(permissionValues).some(
        permission => permission.value !== selectAllValue.value
      )) {
      setValue('selectAll', options[0], {shouldDirty: true})
    } else if (!selectAllValue.value) {
      const selectAllPermission = selectAllOption(permissionValues)

      if (selectAllValue.value !== selectAllPermission.value)
        setValue('selectAll', selectAllPermission, {shouldDirty: true})
    }
  }, [permissionValues, selectAllValue, setValue])

  useEffect(() => {
    if (otherProps.isAddDeputy) return

    setIsSaveButtonDisabled(!isDirty)

    otherProps.onChange(isDirty)
  }, [isDirty])

  const handleSelectAllChange = updatedValue => {
    Object.entries(otherProps.permissions).forEach(([key, value]) =>
      setValue(key, {...value, ...updatedValue}, {shouldDirty: true})
    )
  }

  return (
    <div
      className='manage-permissions-form corp-max-deputies'
      data-testid='manage-permissions-form'>
      {otherProps.children}
      {
        (!otherProps.everplanHasResponses &&
        otherProps.vaultExtension.isEmpty()) &&
          <section className='empty-item-warning-text flex-container'>
            <img src={warningIcon} alt='Warning' />
            <span>You have not added any information to your Everplan.</span>
          </section>
      }
      {
        !(otherProps.itemsWithResponsesByCategory.isEmpty() &&
        otherProps.vaultExtension.isEmpty()) && (
          <FormProvider {...methods} >
            <div className='share-all-note'>
              {'Sharing “All” includes everything in your Everplan. Use the dropdowns to change specific items. If you choose “After death,” you\'ll need to select someone who can report your death and unlock these items.'}
            </div>
            <div className='share-all-container'>
              <h3>All</h3>
              <Controller
                name='selectAll'
                control={control}
                render={({field}) => (
                  <PermissionSelect
                    {...field}
                    classNamePrefix='all-permissions-select'
                    filterOption={option => !!option.value}
                    onChange={value => {
                      field.onChange(value)
                      handleSelectAllChange(value)
                    }}
                    options={options}
                  />
                )}
              />
            </div>
            <ManageVaultExtensionPermission {...otherProps} />
            {
              otherProps.itemsWithResponsesByCategory.map(category => (
                <div key={`${category.get('id')}${category.get('name')}`}>
                  <div className='category-section' >
                    <h3>{category.get('display-name')}</h3>
                    {
                      category.get('items').map(itemResponse => (
                        <ManageItemPermissions
                          key={itemResponse.get('id')}
                          itemResponse={itemResponse}
                          {...otherProps}
                        />
                      ))
                    }
                  </div>
                </div>
              ))
            }
          </FormProvider>
        )
      }
      <ButtonGroup>
        <Button
          color='cancel'
          onClick={otherProps.onCancel}>
          {cancelButtonText}
        </Button>
        <Button
          data-testid='submit-button'
          disabled={isSaveButtonDisabled}
          loading={otherProps.processing}
          onClick={handleSubmit(otherProps.onSubmit)}>
          {buttonText}
        </Button>
      </ButtonGroup>
    </div>
  )
}

ManagePermissionsForm.propTypes = {
  buttonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  everplanHasResponses: PropTypes.bool,
  itemsWithResponsesByCategory: PropTypes.instanceOf(List),
  isAddDeputy: PropTypes.bool,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  permissions: PropTypes.object,
  processing: PropTypes.bool,
  vaultExtension: PropTypes.instanceOf(Map)
}

export default ManagePermissionsForm
